import { useState, type FormEvent } from 'react'
import { Church } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { requireSupabase } from '../lib/supabase'

export function ResetPasswordPage() {
  const { configured, loading, session, recovering, finishRecovery, requestPasswordReset } = useAuth()
  // A fragment is never sent to the web server. Reading it must not verify or consume it.
  const [link] = useState(() => {
    const params = new URLSearchParams(window.location.hash.slice(1))
    const query = new URLSearchParams(window.location.search)
    return {
      token: params.get('type') === 'recovery' ? params.get('token_hash') : null,
      error: params.has('error') || query.has('error'),
    }
  })
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [verified, setVerified] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unusable, setUnusable] = useState(link.error)
  const [complete, setComplete] = useState(false)
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const canReset = !unusable && Boolean(link.token || (recovering && session))

  async function reset(event: FormEvent) {
    event.preventDefault()
    if (submitting || !canReset) return
    setError(null)
    if (password !== confirmation) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      const client = requireSupabase()
      // Only an explicit form submission exchanges the token, even if a scanner runs JavaScript.
      if (link.token && !verified) {
        const { error: verificationError } = await client.auth.verifyOtp({ token_hash: link.token, type: 'recovery' })
        if (verificationError) {
          setUnusable(true)
          throw new Error('This reset link has expired or has already been used. Request a new link below.')
        }
        setVerified(true)
        window.history.replaceState(null, '', '/reset-password')
      }
      const { error: updateError } = await client.auth.updateUser({ password })
      if (updateError) throw updateError
      window.history.replaceState(null, '', '/reset-password')
      finishRecovery()
      setPassword('')
      setConfirmation('')
      setComplete(true)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to reset your password. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  async function request(event: FormEvent) {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await requestPasswordReset(email.trim())
      setSent(true)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to send a reset link. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <section className="login-intro"><div className="brand-mark large" aria-hidden="true"><Church size={28} strokeWidth={1.8} /></div><p className="eyebrow">St. Vincent de Paul</p><h1>Cemetery records, made clear.</h1><p>Secure access to your cemetery records workspace.</p></section>
      <section className="login-panel">
        {complete ? <div className="login-form"><h2>Password updated</h2><p role="status">Your new password has been saved.</p><Link className="primary-button" to="/overview">Continue to records</Link></div> : (
          <form className="login-form" onSubmit={canReset ? reset : request}>
            <div><p className="eyebrow">Staff access</p><h2>Reset your password</h2></div>
            {!configured && <p role="alert">Password reset is unavailable because Supabase is not configured.</p>}
            {loading ? <p role="status">Checking your reset link…</p> : canReset ? <>
              <p>Choose a new password for your account.</p>
              <label>New password<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required autoComplete="new-password" disabled={submitting} /></label>
              <p className="form-note">Use at least 8 characters. Additional account password requirements may apply.</p>
              <label>Confirm new password<input type="password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={8} required autoComplete="new-password" disabled={submitting} /></label>
            </> : <>
              <p>{unusable ? 'This reset link is invalid or expired.' : 'Open the link in your password reset email, or request a new one.'}</p>
              <label>Email address<input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" disabled={submitting} /></label>
              {sent && <p role="status">If an account exists for this email, you will receive a password reset link. Check your inbox and spam folder.</p>}
            </>}
            {error && <div className="form-error" role="alert">{error}</div>}
            <button className="primary-button" type="submit" disabled={!configured || loading || submitting}>{submitting ? 'Please wait…' : canReset ? 'Save new password' : 'Send reset link'}</button>
            <Link to="/login" onClick={() => { window.history.replaceState(null, '', '/reset-password'); finishRecovery() }}>Back to sign in</Link>
          </form>
        )}
      </section>
    </div>
  )
}
